import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Layout } from "../components/Layout";
import { toast, Toaster } from "sonner";
import { MapPin, MedalMilitary, PaperPlaneTilt, CurrencyInr, Users, CheckCircle, Heart } from "@phosphor-icons/react";

const STATUSES = ["applied", "approved", "rejected"];

export default function SponsorshipProgramDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [prog, setProg] = useState(null);
  const [apps, setApps] = useState([]);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/sponsorship-programs/${id}`);
    setProg(data);
    if (user && data.sponsor_id === user.id) {
      const { data: a } = await api.get(`/sponsorship-programs/${id}/applications`);
      setApps(a);
    }
  };
  useEffect(() => { load(); }, [id, user]);

  const apply = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/sponsorship-programs/${id}/apply`, { program_id: id, message });
      toast.success("Applied! The sponsor will review.");
      setMessage("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply");
    } finally { setBusy(false); }
  };

  const updateStatus = async (appId, status) => {
    try {
      await api.put(`/program-applications/${appId}`, { status });
      toast.success(`Marked ${status}${status === "approved" ? " — badge issued" : ""}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  if (!prog) return <Layout><div className="p-10 text-center">Loading…</div></Layout>;
  const owner = user && prog.sponsor_id === user.id;
  const canApply = user && (user.role === "skilled_individual" || user.role === "msme") && prog.status === "open" && prog.slots_used < prog.slots;
  const pct = Math.round((prog.slots_used / prog.slots) * 100);

  return (
    <Layout>
      <Toaster richColors position="top-center" />
      <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div className="as-card p-7">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="as-badge-pill">{prog.sponsor_type.replace("_", " ")}</span>
            {prog.schedule_vii_theme && <span className="as-badge-skill !text-xs">CSR Schedule VII · {prog.schedule_vii_theme}</span>}
          </div>
          <h1 className="text-3xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}} data-testid="program-detail-title">{prog.title}</h1>
          <div className="text-sm text-[var(--as-text-secondary)] mt-2">By <strong className="text-[var(--as-primary)]">{prog.sponsor_name}</strong></div>

          <div className="grid sm:grid-cols-3 gap-4 mt-6">
            <div className="as-card !shadow-none p-4 bg-[var(--as-bg-alt)]">
              <div className="flex items-center gap-2 text-[var(--as-text-secondary)] text-xs font-bold uppercase tracking-wider"><Users size={14} weight="duotone" /> Slots</div>
              <div className="text-2xl font-extrabold text-[var(--as-primary)] mt-1" style={{fontFamily:"Inter"}}>{prog.slots_used}/{prog.slots}</div>
              <div className="h-1.5 bg-white rounded-full overflow-hidden mt-2">
                <div className="h-full bg-[var(--as-accent)]" style={{width: `${pct}%`}} />
              </div>
            </div>
            <div className="as-card !shadow-none p-4 bg-[var(--as-bg-alt)]">
              <div className="flex items-center gap-2 text-[var(--as-text-secondary)] text-xs font-bold uppercase tracking-wider"><CurrencyInr size={14} weight="duotone" /> Per-slot value</div>
              <div className="text-2xl font-extrabold text-[var(--as-primary)] mt-1" style={{fontFamily:"Inter"}}>₹{prog.per_slot_value.toLocaleString("en-IN")}</div>
            </div>
            <div className="as-card !shadow-none p-4 bg-[var(--as-bg-alt)]">
              <div className="flex items-center gap-2 text-[var(--as-text-secondary)] text-xs font-bold uppercase tracking-wider"><MedalMilitary size={14} weight="duotone" /> Badge issued</div>
              <div className="text-sm font-bold text-[var(--as-primary)] mt-1">{prog.co_branded_badge}</div>
            </div>
          </div>

          <p className="mt-6 text-[var(--as-text)] leading-relaxed whitespace-pre-wrap">{prog.description}</p>

          {prog.deliverables?.length > 0 && (
            <div className="mt-6">
              <div className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)] mb-2">What's covered</div>
              <ul className="space-y-1.5">
                {prog.deliverables.map((d) => (
                  <li key={d} className="flex items-center gap-2 text-sm text-[var(--as-primary)]">
                    <CheckCircle size={16} weight="fill" color="#22C55E" /> {d}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">Trades</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {(prog.target_skills || []).map((s) => <span key={s} className="as-badge-skill !text-xs">{s}</span>)}
              </div>
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[var(--as-text-secondary)]">Locations</span>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {(prog.target_locations || []).map((l) => <span key={l} className="as-badge-skill !text-xs"><MapPin size={10} weight="duotone" />{l}</span>)}
              </div>
            </div>
          </div>
        </div>

        {!owner && canApply && (
          <form onSubmit={apply} className="as-card p-7 space-y-4" data-testid="program-apply-form">
            <h2 className="text-xl font-bold text-[var(--as-primary)]" style={{fontFamily:"Inter"}}>Apply for this program</h2>
            <div>
              <label className="as-label">Why are you a fit?</label>
              <textarea className="as-input min-h-[80px]" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="program-apply-msg" />
            </div>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="program-apply-submit">
              <PaperPlaneTilt size={18} weight="duotone" /> {busy ? "..." : "Apply"}
            </button>
          </form>
        )}

        {!user && (
          <div className="as-card p-7 text-center">
            <p className="text-[var(--as-text-secondary)]">Login as a skilled worker or MSME to apply</p>
            <div className="flex gap-2 justify-center mt-3">
              <Link to="/login" className="btn-primary">Login</Link>
              <Link to="/register?role=skilled_individual" className="btn-secondary">Sign up as worker</Link>
            </div>
          </div>
        )}

        {owner && (
          <div className="as-card p-7">
            <h2 className="text-xl font-bold text-[var(--as-primary)] mb-4 flex items-center gap-2" style={{fontFamily:"Inter"}}>
              <Users size={22} weight="duotone" /> Applicants ({apps.length})
            </h2>
            {apps.length === 0 ? <p className="text-[var(--as-text-secondary)]">No applicants yet.</p> : (
              <ul className="divide-y divide-[var(--as-border)]">
                {apps.map((a) => (
                  <li key={a.id} className="py-4 flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`prog-applicant-${a.id}`}>
                    <div>
                      <Link to={`/profile/${a.applicant_id}`} className="font-bold text-[var(--as-primary)] hover:underline">{a.applicant_name}</Link>
                      <div className="text-xs text-[var(--as-text-secondary)]">{a.applicant_role.replace("_"," ")} · {new Date(a.created_at).toLocaleDateString()}</div>
                      {a.message && <p className="text-sm mt-2">{a.message}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {STATUSES.map((s) => (
                        <button key={s} onClick={() => updateStatus(a.id, s)} disabled={a.status === s}
                          className={`px-3 py-1 rounded-lg text-xs font-bold border ${a.status === s ? "bg-[var(--as-primary)] text-[var(--as-bg)] border-[var(--as-primary)]" : "bg-white text-[var(--as-primary)] border-[var(--as-border)] hover:bg-[var(--as-surface-muted)]"}`}
                          data-testid={`prog-status-${s}-${a.id}`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="as-card p-6 bg-[var(--as-bg-alt)] flex items-center gap-3 justify-between">
          <div className="flex items-center gap-3">
            <Heart size={22} weight="duotone" color="#00B7A6" />
            <div>
              <div className="font-bold text-[var(--as-primary)]">Want to support this program?</div>
              <div className="text-xs text-[var(--as-text-secondary)]">Individuals can top up sponsor pools.</div>
            </div>
          </div>
          <Link to={`/donate?program=${prog.id}`} className="btn-primary !py-2 !px-4 text-sm">Donate to this program</Link>
        </div>
      </div>
    </Layout>
  );
}
